import React, { useState } from 'react';
import { authManager } from '../managers/AuthManager';
import './AuthOverlay.css';

const AuthOverlay = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  
  // Forgot Password States
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [pendingPasswordReset, setPendingPasswordReset] = useState(false);
  const [resetCode, setResetCode] = useState('');

  const clearMessages = () => {
    setError('');
    setSuccessMsg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearMessages();
    
    if (!isLogin && password !== confirmPassword) {
      setError("Passwords do not match!");
      return;
    }

    setLoading(true);
    try {
      if (isLogin) {
        await authManager.login(email, password);
      } else {
        await authManager.signUp(email, password, name);
        // Usually Cognito requires email verification.
        // If the pool is configured to verify, we'd handle a verification view.
        // For simplicity, we trigger success or alert them to check their email.
        setPendingVerification(true);
      }
    } catch (err) {
      const errorStr = typeof err === 'string' ? err : (err.message || JSON.stringify(err));
      
      if (isLogin && (errorStr.includes('User does not exist') || errorStr.includes('UserNotFoundException'))) {
        setError('Email does not exist. Please check your email or sign up.');
      } else if (!isLogin && (errorStr.includes('already exists') || errorStr.includes('UsernameExistsException'))) {
        setError('Email already exists. Please log in instead.');
      } else if (isLogin && errorStr.includes('Incorrect username or password')) {
        setError('Incorrect email or password.');
      } else {
        setError(errorStr);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      await authManager.confirmSignUp(email, verificationCode);
      setPendingVerification(false);
      setIsLogin(true);
      setSuccessMsg('Account verified successfully! You can now log in.');
    } catch (err) {
      setError(typeof err === 'string' ? err : (err.message || JSON.stringify(err)));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      await authManager.forgotPassword(email);
      setPendingPasswordReset(true);
    } catch (err) {
      const errorStr = typeof err === 'string' ? err : (err.message || JSON.stringify(err));
      if (errorStr.includes('UserNotFoundException') || errorStr.includes('User does not exist')) {
        setError('Email does not exist.');
      } else {
        setError(errorStr);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPasswordReset = async (e) => {
    e.preventDefault();
    clearMessages();
    
    if (password !== confirmPassword) {
      setError("Passwords do not match!");
      return;
    }

    setLoading(true);
    try {
      await authManager.confirmPasswordReset(email, resetCode, password);
      setPendingPasswordReset(false);
      setIsForgotPassword(false);
      setIsLogin(true);
      setSuccessMsg('Password reset successfully! You can now log in.');
    } catch (err) {
      setError(typeof err === 'string' ? err : (err.message || JSON.stringify(err)));
    } finally {
      setLoading(false);
    }
  };

  if (pendingVerification) {
    return (
      <div className="auth-overlay">
        <div className="auth-card glass-panel">
          <h2 className="auth-title">Verify your email</h2>
          <p className="auth-msg">We've sent a verification code to {email}. Please enter it below.</p>
          
          {error && <div className="auth-error">{error}</div>}

          <form onSubmit={handleVerify} className="auth-form">
            <div className="form-group">
              <label>Verification Code</label>
              <input 
                type="text" 
                placeholder="123456" 
                value={verificationCode} 
                onChange={(e) => setVerificationCode(e.target.value)} 
                required 
              />
            </div>
            <button type="submit" className="auth-btn" disabled={loading}>
              {loading ? "Verifying..." : "Verify Account"}
            </button>
          </form>

          <button className="auth-btn" style={{background: 'transparent', border: '1px solid rgba(255,255,255,0.2)'}} onClick={() => { setPendingVerification(false); setIsLogin(true); }}>
            Cancel / Return to Login
          </button>
        </div>
      </div>
    );
  }

  if (isForgotPassword) {
    if (pendingPasswordReset) {
      return (
        <div className="auth-overlay">
          <div className="auth-card glass-panel">
            <h2 className="auth-title">Reset Password</h2>
            <p className="auth-msg">We've sent a code to {email}. Enter it below with your new password.</p>
            
            {error && <div className="auth-error">{error}</div>}
            
            <form onSubmit={handleConfirmPasswordReset} className="auth-form">
              <div className="form-group">
                <label>Verification Code</label>
                <input type="text" placeholder="123456" value={resetCode} onChange={(e) => setResetCode(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>New Password</label>
                <input type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Confirm New Password</label>
                <input type="password" placeholder="••••••••" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
              </div>
              <button type="submit" className="auth-btn" disabled={loading}>
                {loading ? "Resetting..." : "Reset Password"}
              </button>
            </form>
            <button className="auth-btn" style={{background: 'transparent', border: '1px solid rgba(255,255,255,0.2)'}} onClick={() => { setPendingPasswordReset(false); setIsForgotPassword(false); clearMessages(); }}>
              Cancel
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="auth-overlay">
        <div className="auth-card glass-panel">
          <h2 className="auth-title">Forgot Password</h2>
          <p className="auth-msg">Enter your email and we'll send you a code to reset your password.</p>
          
          {error && <div className="auth-error">{error}</div>}
          
          <form onSubmit={handleForgotPassword} className="auth-form">
            <div className="form-group">
              <label>Email Address</label>
              <input type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <button type="submit" className="auth-btn" disabled={loading}>
              {loading ? "Sending..." : "Send Reset Code"}
            </button>
          </form>
          <button className="auth-btn" style={{background: 'transparent', border: '1px solid rgba(255,255,255,0.2)'}} onClick={() => { setIsForgotPassword(false); clearMessages(); }}>
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-overlay">
      <div className="auth-card glass-panel">
        <div className="auth-header">
          <h2 className="auth-title">{isLogin ? "Welcome Back" : "Create Account"}</h2>
          <p className="auth-subtitle">
            {isLogin ? "Sign in to access your AI Resume Creator." : "Sign up to start building your professional profile."}
          </p>
        </div>
        
        {error && <div className="auth-error">{error}</div>}
        {successMsg && <div className="auth-success">{successMsg}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          {!isLogin && (
            <div className="form-group">
              <label>Full Name</label>
              <input 
                type="text" 
                placeholder="John Doe" 
                value={name} 
                onChange={(e) => setName(e.target.value)} 
                required 
              />
            </div>
          )}

          <div className="form-group">
            <label>Email Address</label>
            <input 
              type="email" 
              placeholder="you@example.com" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              required 
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input 
              type="password" 
              placeholder="••••••••" 
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
              required 
            />
          </div>

          {!isLogin && (
            <div className="form-group">
              <label>Confirm Password</label>
              <input 
                type="password" 
                placeholder="••••••••" 
                value={confirmPassword} 
                onChange={(e) => setConfirmPassword(e.target.value)} 
                required 
              />
            </div>
          )}

          <button type="submit" className="auth-btn" disabled={loading}>
            {loading ? "Processing..." : (isLogin ? "Sign In" : "Sign Up")}
          </button>
        </form>

        <div className="auth-toggle">
          {isLogin && (
            <div style={{ marginBottom: '10px' }}>
              <span className="toggle-link" onClick={() => { setIsForgotPassword(true); clearMessages(); }}>
                Forgot Password?
              </span>
            </div>
          )}
          {isLogin ? "Don't have an account? " : "Already have an account? "}
          <span className="toggle-link" onClick={() => { setIsLogin(!isLogin); clearMessages(); }}>
            {isLogin ? "Sign Up" : "Log In"}
          </span>
        </div>
      </div>
    </div>
  );
};

export default AuthOverlay;
